// Offline Sync Service
// Enables offline-first functionality with automatic sync when connectivity returns

import { supabase } from './supabase';
import { supabaseData } from './supabaseData';

const QUEUE_KEY = 'workvan_sync_queue';
const LAST_SYNC_KEY = 'workvan_last_sync';

// Track online status - start with navigator.onLine but verify with actual request
let isOnlineStatus = typeof navigator !== 'undefined' ? navigator.onLine : true;
let syncInProgress = false;
let listeners = [];
let connectivityCheckInterval = null;

// Generate unique ID for queue items
const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Actually verify connectivity by making a real request
// navigator.onLine is unreliable on mobile devices
const verifyConnectivity = async () => {
  try {
    // Try to reach Supabase - if this works, we're really online
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL || 'https://wrgmicftdykgaygbnkqn.supabase.co'}/rest/v1/`, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-store'
    });

    clearTimeout(timeout);

    const wasOffline = !isOnlineStatus;
    isOnlineStatus = response.ok || response.status === 401; // 401 means server responded

    if (wasOffline && isOnlineStatus) {
      console.log('[OfflineService] Connectivity restored');
      notifyListeners();
      processQueue();
    } else if (!wasOffline && !isOnlineStatus) {
      console.log('[OfflineService] Connectivity lost');
      notifyListeners();
    }

    return isOnlineStatus;
  } catch (e) {
    // Network error - we're offline
    if (isOnlineStatus) {
      isOnlineStatus = false;
      notifyListeners();
    }
    return false;
  }
};

// Initialize online/offline listeners
if (typeof window !== 'undefined') {
  // Still listen to browser events but verify with actual request
  window.addEventListener('online', async () => {
    // Verify we're actually online with a real request
    await verifyConnectivity();
  });

  window.addEventListener('offline', () => {
    isOnlineStatus = false;
    notifyListeners();
  });

  // Periodically check connectivity (every 30 seconds when supposedly offline)
  // This catches cases where navigator.onLine is wrong
  const startConnectivityCheck = () => {
    if (connectivityCheckInterval) return;
    connectivityCheckInterval = setInterval(() => {
      if (!isOnlineStatus) {
        verifyConnectivity();
      }
    }, 30000);
  };

  // Start periodic checking after a short delay
  setTimeout(startConnectivityCheck, 5000);

  // Verify connectivity immediately on load
  // This catches cases where navigator.onLine is wrong from the start
  setTimeout(() => verifyConnectivity(), 100);

  // Also verify on page visibility change (user returns to app)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      verifyConnectivity();
    }
  });
}

// Notify all listeners of status change
const notifyListeners = () => {
  listeners.forEach(callback => {
    try {
      callback(getStatus());
    } catch (e) {
    }
  });
};

// Subscribe to sync status changes
export const onSyncStatusChange = (callback) => {
  listeners.push(callback);
  // Return unsubscribe function
  return () => {
    listeners = listeners.filter(cb => cb !== callback);
  };
};

// Get current sync status
export const getStatus = () => {
  const queue = getQueue();
  const pendingCount = queue.filter(op => op.status === 'pending').length;
  const failedCount = queue.filter(op => op.status === 'failed').length;
  const lastSync = localStorage.getItem(LAST_SYNC_KEY);

  return {
    isOnline: isOnlineStatus,
    isSyncing: syncInProgress,
    pendingCount,
    failedCount,
    lastSync: lastSync ? new Date(lastSync) : null,
    totalQueued: queue.length
  };
};

// Check if online
export const isOnline = () => isOnlineStatus;

// Get the queue from localStorage
const getQueue = () => {
  try {
    const data = localStorage.getItem(QUEUE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
};

// Save queue to localStorage
const saveQueue = (queue) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    notifyListeners();
  } catch (e) {
  }
};

// Add operation to queue
const addToQueue = (operation) => {
  const queue = getQueue();
  const queueItem = {
    id: generateId(),
    timestamp: new Date().toISOString(),
    status: 'pending',
    retryCount: 0,
    error: null,
    ...operation
  };
  queue.push(queueItem);
  saveQueue(queue);
  return queueItem;
};

// Update queue item
const updateQueueItem = (id, updates) => {
  const queue = getQueue();
  const index = queue.findIndex(op => op.id === id);
  if (index !== -1) {
    queue[index] = { ...queue[index], ...updates };
    saveQueue(queue);
  }
};

// Remove item from queue
const removeFromQueue = (id) => {
  const queue = getQueue();
  const filtered = queue.filter(op => op.id !== id);
  saveQueue(filtered);
};

// Timeout wrapper for Supabase operations (iOS Safari can hang indefinitely)
const withTimeout = (promise, ms = 15000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out - will retry when online')), ms)
    )
  ]);
};

// Execute a single Supabase operation
const executeOperation = async (op) => {
  const { table, operation, data, filters } = op;

  try {
    let result;

    switch (operation) {
      case 'INSERT':
        // Remove temp_id before inserting
        const insertData = { ...data };
        delete insertData.temp_id;
        result = await withTimeout(supabaseData.from(table).insert([insertData]).select().single());
        break;

      case 'UPDATE':
        if (!filters?.id) {
          throw new Error('UPDATE requires filters.id');
        }
        result = await withTimeout(supabaseData.from(table).update(data).eq('id', filters.id).select().single());
        break;

      case 'DELETE':
        if (!filters?.id) {
          throw new Error('DELETE requires filters.id');
        }
        result = await withTimeout(supabaseData.from(table).delete().eq('id', filters.id));
        break;

      case 'BULK_INSERT':
        result = await withTimeout(supabaseData.from(table).insert(data));
        break;

      default:
        throw new Error(`Unknown operation: ${operation}`);
    }

    if (result.error) {
      throw result.error;
    }

    return { success: true, data: result.data };
  } catch (error) {
    console.warn('[OfflineService] Operation failed:', error.message);
    return { success: false, error: error.message || 'Unknown error' };
  }
};

// Process the entire queue
export const processQueue = async () => {
  if (syncInProgress) {
    return;
  }

  if (!isOnlineStatus) {
    return;
  }

  const queue = getQueue();
  const pending = queue.filter(op => op.status === 'pending' || op.status === 'failed');

  if (pending.length === 0) {
    return;
  }

  syncInProgress = true;
  notifyListeners();

  // Track temp_id to real_id mappings for dependent operations
  const idMappings = {};

  // Sort by timestamp to maintain order
  pending.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  for (const op of pending) {
    // Skip if we've exceeded retry limit
    if (op.retryCount >= 3) {
      continue;
    }

    updateQueueItem(op.id, { status: 'syncing' });

    // Replace any temp_id references with real IDs
    let processedOp = { ...op };
    if (op.dependsOn && idMappings[op.dependsOn]) {
      // This operation depends on another - replace the foreign key
      if (op.table === 'quote_items' && op.data) {
        processedOp.data = Array.isArray(op.data)
          ? op.data.map(item => ({ ...item, quote_id: idMappings[op.dependsOn] }))
          : { ...op.data, quote_id: idMappings[op.dependsOn] };
      }
    }

    const result = await executeOperation(processedOp);

    if (result.success) {
      // Store ID mapping if this was a temp_id operation
      if (op.data?.temp_id && result.data?.id) {
        idMappings[op.data.temp_id] = result.data.id;
      }

      removeFromQueue(op.id);
    } else {
      updateQueueItem(op.id, {
        status: 'failed',
        retryCount: op.retryCount + 1,
        error: result.error
      });
    }
  }

  syncInProgress = false;
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
  notifyListeners();
};

// Main entry point - save with sync support
export const saveWithSync = async (table, operation, data, filters = null, options = {}) => {
  const { dependsOn = null } = options;

  // If we think we're offline, do a quick connectivity check first
  // navigator.onLine is unreliable on mobile
  if (!isOnlineStatus) {
    await verifyConnectivity();
  }

  // If online, try to execute immediately
  if (isOnlineStatus && !syncInProgress) {
    const result = await executeOperation({ table, operation, data, filters });

    if (result.success) {
      return {
        data: result.data,
        error: null,
        queued: false,
        tempId: null
      };
    }

    // If failed and it's a network/timeout error, queue it
    const isNetworkError = result.error?.includes('network') ||
                           result.error?.includes('fetch') ||
                           result.error?.includes('timed out') ||
                           result.error?.includes('Failed to fetch');
    if (!isNetworkError) {
      // Real error, don't queue
      return {
        data: null,
        error: result.error,
        queued: false,
        tempId: null
      };
    }
    // Network/timeout error - fall through to queue the operation
  }

  // Queue the operation for later
  const tempId = operation === 'INSERT' ? `temp_${generateId()}` : null;
  const queueData = tempId ? { ...data, temp_id: tempId } : data;

  addToQueue({
    table,
    operation,
    data: queueData,
    filters,
    dependsOn
  });

  // Return a mock successful response for UI continuity
  return {
    data: tempId ? { ...data, id: tempId } : data,
    error: null,
    queued: true,
    tempId
  };
};

// Convenience methods for common operations
export const insertWithSync = (table, data, options) =>
  saveWithSync(table, 'INSERT', data, null, options);

export const updateWithSync = (table, id, data) =>
  saveWithSync(table, 'UPDATE', data, { id });

export const deleteWithSync = (table, id) =>
  saveWithSync(table, 'DELETE', null, { id });

export const bulkInsertWithSync = (table, dataArray, options) =>
  saveWithSync(table, 'BULK_INSERT', dataArray, null, options);

// Clear failed items from queue
export const clearFailedItems = () => {
  const queue = getQueue();
  const filtered = queue.filter(op => op.status !== 'failed');
  saveQueue(filtered);
};

// Manual retry of failed items
export const retryFailed = async () => {
  const queue = getQueue();
  queue.forEach(op => {
    if (op.status === 'failed') {
      updateQueueItem(op.id, { status: 'pending', retryCount: 0 });
    }
  });
  await processQueue();
};

// Get pending count (for UI badge)
export const getPendingCount = () => {
  const queue = getQueue();
  return queue.filter(op => op.status === 'pending' || op.status === 'failed').length;
};

// Clear entire queue (use with caution)
export const clearQueue = () => {
  localStorage.removeItem(QUEUE_KEY);
  notifyListeners();
};

// Export for debugging
export const getQueueContents = () => getQueue();
